/**
 * This file is included in `/next.config.mjs` which ensures the app isn't built with invalid env vars.
 * It has to be a `.ts`-file to be imported in the new-code structure.
 */
import type {z} from 'zod'
import {serverSchema} from './schema.mjs'
import {env as clientEnv, formatErrors} from './client'

type ServerEnv = z.infer<typeof serverSchema>

const isTestEnvironment = process.env.NODE_ENV === 'test'

const parsedServerEnv = serverSchema.safeParse(process.env)

if (!parsedServerEnv.success && !isTestEnvironment) {
	const message = `❌ Invalid environment variables:\n${formatErrors(
		parsedServerEnv.error.format() as Record<string, unknown>
	).join('')}`
	// eslint-disable-next-line no-console -- We absolutely want to console log here so we can fix the issue
	console.error(
		'❌ Invalid environment variables:\n',
		...formatErrors(
			parsedServerEnv.error.format() as Record<string, unknown>
		)
	)
	throw new Error(message)
}

// Default test values for when env vars are not available during testing
const serverTestDefaults: ServerEnv = {
	ENABLE_CONSOLE_LOG: false,
	KONTENT_PREVIEW_API_KEY: 'test-preview-key',
	KONTENT_PREVIEW_SECRET: 'test-preview-secret',
	GLOBAL_CONFIG_NAME: 'global_config',
	CDC_DATACENTER: 'us1.gigya.com',
	CDC_DEBUG_PAYLOADS: false,
	CDC_API_USER_KEY: 'test-cdc-user-key',
	CDC_API_SECRET: 'test-cdc-secret',
	DISEASE_API: 'https://test-disease-api.example.com',
	DISEASE_API_SUBSCRIPTIONKEY: 'test-disease-key',
	DISEASE_API_TOKEN_URL: 'https://test-token.example.com',
	DISEASE_API_CLIENTID: 'test-disease-client-id',
	DISEASE_API_CLIENTSECRET: 'test-disease-secret',
	FIND_A_BUSINESSMAP_URL: 'https://test-business-map.example.com',
	FIND_A_BUSINESS_API_KEY: 'test-business-key',
	FIND_A_BUSINESS_API_CLIENT_ID: 'test-business-client-id',
	FIND_A_BUSINESS_API_CLIENT_SECRET: 'test-business-secret',
	FIND_A_BUSINESS_API_TOKEN_URL: 'https://test-business-token.example.com',
	SENDGRID_API_KEY: 'SG.test-sendgrid-key',
	GOOGLE_GEOCODE_KEY: 'test-geocode-key',
	PET_ENDPOINT: 'https://test-pet-endpoint.example.com',
	VEEVA_DOMAIN: 'test-veeva-domain',
	VEEVA_VER: 'v1',
	TENANT_ID: 'test-tenant-id',
	LAKEHOUSE_API: 'https://test-lakehouse.example.com',
	LAKEHOUSE_API_SUBSCRIPTIONKEY: 'test-lakehouse-key',
	CAMPAIGN_INTEREST_DATABASE: 'test-database',
	CAMPAIGN_INTEREST_CONTAINER: 'test-container',
	CAMPAIGN_INTEREST_ENDPOINT: 'https://test-endpoint.example.com',
	CAMPAIGN_INTEREST_KEY: 'test-campaign-key',
	WEB_ACTIVITY_DATA_PRODUCT_API_URL: 'https://test-web-activity.example.com',
	WEB_ACTIVITY_DATA_PRODUCT_API_CLIENT_ID:
		'00000000-0000-0000-0000-000000000000',
	WEB_ACTIVITY_DATA_PRODUCT_API_CLIENT_SECRET: 'test-web-activity-secret',
	WEB_ACTIVITY_DATA_PRODUCT_API_TOKEN_URL:
		'https://test-web-activity-token.example.com',
	IDGW_API_SCOPE: 'test-idgw-scope',
	IDGW_AZURE_CLIENT: 'test-azure-client',
	IDGW_AZURE_SECRET: 'test-azure-secret',
	IDGW_AZURE_TENANT: 'test-azure-tenant',
	IDGW_CAMPAIGN_URL: 'https://test-idgw-campaign.example.com',
	APP_ENVIRONMENT: 'test',
}

const getServerEnvData = (): ServerEnv => {
	if (parsedServerEnv.success) {
		return parsedServerEnv.data
	}
	if (isTestEnvironment) {
		return {
			...serverTestDefaults,
			...(process.env as unknown as Partial<ServerEnv>),
		}
	}
	// This should never be reached due to the throw above, but TypeScript needs it
	return serverTestDefaults
}

const serverEnvData = getServerEnvData()

for (const key of Object.keys(serverEnvData)) {
	if (key.startsWith('NEXT_PUBLIC_')) {
		// eslint-disable-next-line no-console -- We absolutely want to console log here so we can fix the issue
		console.warn('❌ You are exposing a server-side env-variable:', key)

		throw new Error('You are exposing a server-side env-variable')
	}
}

export const env: ServerEnv & typeof clientEnv = {
	...serverEnvData,
	...clientEnv,
}
